/**
 * AI Chat API Service
 *
 * v3: 独自 Claude API + RAG (POST /api/v3/chat/stream, SSE)
 * v1: 旧 Dify プロキシ (POST /api/ai/chat) — フォールバック・後方互換のため残置
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://struct-code-production.up.railway.app';

class AIService {
  _getToken() {
    return localStorage.getItem('struct-code-token');
  }

  _authHeaders() {
    const token = this._getToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  /* ---------------------------------------------------------------- */
  /* Usage / Health                                                   */
  /* ---------------------------------------------------------------- */

  async getUsage() {
    const res = await fetch(`${API_BASE_URL}/api/ai/usage`, {
      headers: this._authHeaders(),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const err = new Error(data.detail || 'Failed to get usage');
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  async getV3Health() {
    const res = await fetch(`${API_BASE_URL}/api/v3/chat/health`);
    return res.json();
  }

  /* ---------------------------------------------------------------- */
  /* v3: Claude API + RAG (推奨)                                       */
  /* ---------------------------------------------------------------- */

  /**
   * /api/v3/chat/stream への SSE リクエスト。
   *
   * @param {Object}   args
   * @param {Array}    args.messages  会話履歴 [{role:'user'|'assistant', content:string}]
   * @param {Object}   [args.context] ユーザーコンテキスト (type_code, axes, etc.)
   * @param {AbortSignal} [args.signal]
   * @returns {Promise<Response>} 呼び出し側で reader を取って SSE をパースする
   */
  streamChatV3({ messages, context = null, conversationId = null, signal } = {}) {
    return fetch(`${API_BASE_URL}/api/v3/chat/stream`, {
      method: 'POST',
      headers: this._authHeaders(),
      body: JSON.stringify({ messages, context, conversation_id: conversationId }),
      signal,
    });
  }

  /**
   * SSE レスポンスをパースして async generator で順次デルタを yield する。
   * 呼び出し側は for-await で受け取れる。
   *
   * イベント形式:
   *   data: {"type":"delta","text":"..."}
   *   data: {"type":"done"}
   *   data: {"type":"error","message":"..."}
   */
  async *iterateSSE(response) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const raw of lines) {
        const line = raw.trim();
        if (!line.startsWith('data:')) continue;
        const jsonStr = line.slice(5).trim();
        if (!jsonStr) continue;
        try {
          const parsed = JSON.parse(jsonStr);
          yield parsed;
          if (parsed.type === 'done' || parsed.type === 'error') return;
        } catch {
          // skip malformed line
        }
      }
    }
  }

  /* ---------------------------------------------------------------- */
  /* v3: 会話履歴 (アカウントに紐づく・端末をまたぐ)                    */
  /* ---------------------------------------------------------------- */

  /** 認証ユーザー自身の会話一覧 (本文なし)。未認証なら 401。 */
  async listConversations() {
    const res = await fetch(`${API_BASE_URL}/api/v3/chat/conversations`, {
      headers: this._authHeaders(),
    });
    if (!res.ok) {
      const err = new Error('Failed to list conversations');
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  /** 1 会話をメッセージ全文つきで取得。 */
  async getConversation(conversationId) {
    const res = await fetch(
      `${API_BASE_URL}/api/v3/chat/conversations/${encodeURIComponent(conversationId)}`,
      { headers: this._authHeaders() },
    );
    if (!res.ok) {
      const err = new Error('Failed to get conversation');
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  /** サーバー側の会話を削除。成功可否を boolean で返す (例外は投げない)。 */
  async deleteConversation(conversationId) {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v3/chat/conversations/${encodeURIComponent(conversationId)}`,
        { method: 'DELETE', headers: this._authHeaders() },
      );
      return res.ok;
    } catch {
      return false;
    }
  }

}

const aiService = new AIService();
export default aiService;
